import React, { useState, useCallback } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getReciprocityDetail,
  updateReciprocity,
  addReciprocityMember,
  updateReciprocityContact,
  deleteReciprocityContact,
  replaceReciprocityMember,
  addReciprocityChapter,
  removeReciprocityChapter,
  sendReciprocityMessage,
  getReciprocityRoles,
} from '../../../endpoints/reciprocity';
import { reciprocityTypes } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import ChapterSelector from '../../../Components/ChapterSelector';
import AlertItem from '../../../Components/AlertItem';
import {
  ReciprocityDetail as ReciprocityDetailType,
  ReciprocityMessage,
  ReciprocityRole,
} from '../../../types/reciprocity';

export default function ReciprocityDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();

  // State management
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<ReciprocityDetailType>>({});
  const [localChapterSearch, setLocalChapterSearch] = useState(false);
  const [localChapter, setLocalChapter] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageText, setMessageText] = useState('');

  // API hooks
  const {
    data: reciprocityData,
    isLoading,
    refetch,
  } = useApiGet<ReciprocityDetailType>(
    ['reciprocity-detail', id],
    getReciprocityDetail,
    {
      enabled: !!id,
      retry: 1,
    },
    id!
  );

  const { data: reciprocityRoles } = useApiGet<ReciprocityRole[]>(
    ['reciprocity-roles'],
    getReciprocityRoles,
    {
      enabled: !!reciprocityData,
      retry: 1,
    }
  );

  // Helper function to check if user can edit
  // Uses the permission flag from the backend API response
  const canEdit = useCallback(() => {
    return reciprocityData?.can_edit_reciprocity ?? false;
  }, [reciprocityData]);

  // Helper function to check if user can edit financial fields
  // Uses the permission flag from the backend API response
  const canEditFinancial = useCallback(() => {
    return reciprocityData?.can_edit_financial ?? false;
  }, [reciprocityData]);

  const { mutate: updateReciprocityMutation, isPending: isUpdating } =
    useApiSend(
      (params: { id: string; data: any }) =>
        updateReciprocity(params.id, params.data),
      () => {
        setIsEditing(false);
        setSuccessMessage('Reciprocity updated successfully!');
        refetch();
        setTimeout(() => setSuccessMessage(''), 3000);
      },
      (error: Error) => {
        setErrorMessage('Failed to update reciprocity. Please try again.');
        console.error('Error updating reciprocity:', error);
      }
    );

  const { mutate: addMember, isPending: isAddingMember } = useApiSend(
    (params: { id: string; data: any }) =>
      addReciprocityMember(params.id, params.data),
    () => {
      setSuccessMessage('Member added successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to add member. Please try again.');
      console.error('Error adding member:', error);
    }
  );

  const { mutate: updateContact, isPending: isUpdatingContact } = useApiSend(
    (params: { id: string; memberId: string; data: any }) =>
      updateReciprocityContact(params.id, params.memberId, params.data),
    () => {
      setSuccessMessage('Contact updated successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to update contact. Please try again.');
      console.error('Error updating contact:', error);
    }
  );

  const { mutate: deleteContact, isPending: isDeletingContact } = useApiSend(
    (params: { id: string; memberId: string }) =>
      deleteReciprocityContact(params.id, params.memberId),
    () => {
      setSuccessMessage('Contact removed successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to remove contact. Please try again.');
      console.error('Error removing contact:', error);
    }
  );

  const { mutate: replaceMember, isPending: isReplacingMember } = useApiSend(
    (params: { id: string; memberId: string; data: any }) =>
      replaceReciprocityMember(params.id, params.memberId, params.data),
    () => {
      setSuccessMessage('Member replaced successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to replace member. Please try again.');
      console.error('Error replacing member:', error);
    }
  );

  const { mutate: addChapter, isPending: isAddingChapter } = useApiSend(
    (params: { id: string; data: any }) =>
      addReciprocityChapter(params.id, params.data),
    () => {
      setLocalChapterSearch(false);
      setLocalChapter('');
      setSuccessMessage('Chapter added successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to add chapter. Please try again.');
      console.error('Error adding chapter:', error);
    }
  );

  const { mutate: removeChapter, isPending: isRemovingChapter } = useApiSend(
    (params: { id: string; chapterId: string }) =>
      removeReciprocityChapter(params.id, params.chapterId),
    () => {
      setSuccessMessage('Chapter removed successfully!');
      refetch();
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to remove chapter. Please try again.');
      console.error('Error removing chapter:', error);
    }
  );

  const { mutate: sendMessage, isPending: isSendingMessage } = useApiSend(
    (params: { id: string; data: ReciprocityMessage }) =>
      sendReciprocityMessage(params.id, params.data),
    () => {
      setShowMessageDialog(false);
      setMessageText('');
      setSuccessMessage('Message sent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    (error: Error) => {
      setErrorMessage('Failed to send message. Please try again.');
      console.error('Error sending message:', error);
    }
  );

  // Event handlers
  const handleEdit = useCallback(() => {
    if (reciprocityData) {
      setEditData({
        name: reciprocityData.name,
        reciprocity_type_id: reciprocityData.reciprocity_type_id,
        is_active: reciprocityData.is_active,
        financial_name: reciprocityData.financial_name,
        tax_id_number: reciprocityData.tax_id_number,
      });
      setIsEditing(true);
    }
  }, [reciprocityData]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditData({});
  }, []);

  const handleSave = useCallback(() => {
    if (
      id &&
      editData.name &&
      editData.reciprocity_type_id !== undefined &&
      editData.is_active !== undefined
    ) {
      const updateData: any = {
        name: editData.name,
        reciprocity_type_id: editData.reciprocity_type_id,
        is_active: editData.is_active,
      };

      // Only include financial fields if user has permission
      if (canEditFinancial()) {
        if (editData.financial_name !== undefined) {
          updateData.financial_name = editData.financial_name || null;
        }
        if (editData.tax_id_number !== undefined) {
          updateData.tax_id_number = editData.tax_id_number || null;
        }
      }

      updateReciprocityMutation({
        id,
        data: updateData,
      });
    }
  }, [id, editData, updateReciprocityMutation, canEditFinancial]);

  const formatDateForAPI = (
    dateString: string | null | undefined
  ): string | null => {
    if (!dateString) return null;
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
  };

  const onSavePosition = useCallback(
    (position: any) => {
      if (!id) return;

      if (position.action === 'newAppointment') {
        if (!position.id) {
          // Handle new appointment
          addMember({
            id,
            data: {
              user_id: position.userId,
              start_date: formatDateForAPI(position.startDate) || '',
              role_id: position.positionId,
            },
          });
        } else {
          // Handle replace
          replaceMember({
            id,
            memberId: position.id,
            data: {
              user_id: position.userId,
              start_date: formatDateForAPI(position.startDate) || '',
              end_date: formatDateForAPI(position.endDate) || null,
            },
          });
        }
      } else if (position.action === 'editDates') {
        // Handle patch
        updateContact({
          id,
          memberId: position.id,
          data: {
            start_date: formatDateForAPI(position.startDate) || '',
            end_date: formatDateForAPI(position.endDate) || null,
          },
        });
      } else if (position.action === 'removeRecord') {
        // Handle delete
        deleteContact({
          id,
          memberId: position.id,
        });
      }
    },
    [id, addMember, replaceMember, updateContact, deleteContact]
  );

  const handleAddChapter = useCallback(() => {
    if (id && localChapter) {
      addChapter({
        id,
        data: { chapter_id: localChapter },
      });
    }
  }, [id, localChapter, addChapter]);

  const handleRemoveChapter = useCallback(
    (chapterId: string) => {
      if (id) {
        removeChapter({ id, chapterId });
      }
    },
    [id, removeChapter]
  );

  const handleSendMessage = useCallback(() => {
    if (id && messageText.trim()) {
      sendMessage({
        id,
        data: { message: messageText.trim() },
      });
    }
  }, [id, messageText, sendMessage]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM DD, YYYY');
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!reciprocityData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <AlertItem
          severity="error"
          description="Reciprocity not found."
          onClose={() => {}}
          keepOpen={true}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          color="primary"
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          {reciprocityData.name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information - Full Width */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardHeader
              title="Basic Information"
              avatar={<PersonIcon color="primary" />}
            />
            <CardContent>
              <Grid container spacing={3}>
                {isEditing ? (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={editData.name || ''}
                        onChange={(e) =>
                          setEditData({ ...editData, name: e.target.value })
                        }
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={editData.reciprocity_type_id || ''}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              reciprocity_type_id: e.target.value,
                            })
                          }
                          label="Type"
                        >
                          <MenuItem value={reciprocityTypes.Reciprocity}>
                            Reciprocity
                          </MenuItem>
                          <MenuItem value={reciprocityTypes.Group}>
                            Group
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={editData.is_active ? 'true' : 'false'}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              is_active: e.target.value === 'true',
                            })
                          }
                          label="Status"
                        >
                          <MenuItem value="true">Active</MenuItem>
                          <MenuItem value="false">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {canEditFinancial() && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Financial Name"
                            value={editData.financial_name || ''}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                financial_name: e.target.value,
                              })
                            }
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Tax ID Number"
                            value={editData.tax_id_number || ''}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                tax_id_number: e.target.value,
                              })
                            }
                            size="small"
                          />
                        </Grid>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body2">
                          {reciprocityData.name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Type
                        </Typography>
                        <Typography variant="body2">
                          {reciprocityData.reciprocity_type}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={
                            reciprocityData.is_active ? 'Active' : 'Inactive'
                          }
                          color={
                            reciprocityData.is_active ? 'success' : 'default'
                          }
                          size="small"
                          sx={{ mt: 0.5, ml: 0.5 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          S/P/D Chapter
                        </Typography>
                        <Typography variant="body2">
                          {reciprocityData.spd_chapter_name || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Date Started
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(reciprocityData.start_date)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Date Ended
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(reciprocityData.end_date)}
                        </Typography>
                      </Box>
                    </Grid>
                    {reciprocityData.financial_name && (
                      <Grid item xs={12} md={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Financial Name
                          </Typography>
                          <Typography variant="body2">
                            {reciprocityData.financial_name}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {reciprocityData.tax_id_number && (
                      <Grid item xs={12} md={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Tax ID Number
                          </Typography>
                          <Typography variant="body2">
                            {reciprocityData.tax_id_number}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </CardContent>
            <CardActions>
              {canEdit() && isEditing ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ width: '100%' }}
                >
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={isUpdating}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ width: '100%', textAlign: 'right' }}
                >
                  {canEdit() && (
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleEdit}
                      disabled={isUpdating}
                    >
                      Edit
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<MessageIcon />}
                    onClick={() => setShowMessageDialog(true)}
                    disabled={isSendingMessage}
                  >
                    Message Reciprocity
                  </Button>
                </Stack>
              )}
            </CardActions>
          </Card>
        </Grid>

        {/* Messages */}
        {errorMessage && (
          <Grid item xs={12}>
            <AlertItem
              severity="error"
              description={errorMessage}
              onClose={() => setErrorMessage('')}
            />
          </Grid>
        )}
        {successMessage && (
          <Grid item xs={12}>
            <AlertItem
              severity="success"
              description={successMessage}
              onClose={() => setSuccessMessage('')}
            />
          </Grid>
        )}

        {/* Contacts and Chapters */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Contacts Section */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardHeader
                  title="Contacts"
                  avatar={<PersonIcon color="primary" />}
                />
                <CardContent>
                  <Stack spacing={2}>
                    {reciprocityData.contacts
                      .sort((a, b) => a.order - b.order)
                      .map((contact) => (
                        <PositionDisplay
                          key={contact.id}
                          id={contact.id}
                          position={contact.role}
                          positionId={contact.role_id}
                          committeeId={null}
                          name={contact.name}
                          startDate={contact.start_date}
                          endDate={contact.end_date}
                          canEdit={canEdit()}
                          isSaving={
                            isAddingMember ||
                            isUpdatingContact ||
                            isDeletingContact ||
                            isReplacingMember
                          }
                          errorSavingText={errorMessage}
                          onSave={onSavePosition}
                          userId={contact.user_id}
                          pageFrom="reciprocity"
                          canMessage={false}
                        />
                      ))}
                    {canEdit() && (
                      <AddPosition
                        buttonText="Add Position"
                        isSaving={isAddingMember}
                        positions={reciprocityRoles || []}
                        onAdd={onSavePosition}
                        savingError={errorMessage}
                        saveSuccess={successMessage}
                        committeeId={null}
                        spdChapterId={null}
                        localChapterId={null}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Chapters Section */}
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardHeader
                  title="Local Chapters"
                  avatar={<LocationOnIcon color="primary" />}
                />
                <CardContent>
                  <Stack spacing={2}>
                    {reciprocityData.chapters
                      .sort((a, b) => a.order - b.order)
                      .map((chapter) => (
                        <Box
                          key={chapter.chapter_id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {chapter.chapter_name}
                          </Typography>
                          {canEdit() && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveChapter(chapter.id)}
                              disabled={isRemovingChapter}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    {reciprocityData.chapters.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                      >
                        No local chapters assigned
                      </Typography>
                    )}
                    {canEdit() && (
                      <Box>
                        {localChapterSearch ? (
                          <Stack spacing={2}>
                            <ChapterSelector
                              chapter={localChapter}
                              handleChapterSelect={(chapterId) =>
                                setLocalChapter(chapterId || '')
                              }
                              spd=""
                              keyId={id}
                              label="Select Local Chapter"
                            />
                            {localChapter && (
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<AddIcon />}
                                onClick={handleAddChapter}
                                disabled={isAddingChapter}
                                size="small"
                              >
                                Add Chapter
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                setLocalChapterSearch(false);
                                setLocalChapter('');
                              }}
                              disabled={isAddingChapter}
                              size="small"
                            >
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<AddIcon />}
                            onClick={() => setLocalChapterSearch(true)}
                            disabled={isAddingChapter}
                            size="small"
                          >
                            Add Chapter
                          </Button>
                        )}
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Message Dialog */}
      <Dialog
        open={showMessageDialog}
        onClose={() => setShowMessageDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Message Reciprocity</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Enter your message here..."
            inputProps={{ maxLength: 500 }}
            helperText={`${messageText.length}/500 characters`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setShowMessageDialog(false);
              setMessageText('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            disabled={!messageText.trim() || isSendingMessage}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
